// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useParams, useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, IconButton, TextField, Tooltip } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// react icons
import { MdDelete } from "react-icons/md";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import { StyledLink } from "components/shared/Link";
import DeleteModal from "components/shared/Modal/Delete";

// helper
import Toast from "helper/toast";
import GenerateHeadCell from "helper/generate-head-cell";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
} from "./editCategory.style";

// request methods
import {
  deleteProductsCategoryRequest,
  editProductsCategoryRequest,
  getProductCategoryByIdRequest,
} from "api/requests/products";

// validation
import { categorySchema, validateImg } from "validations";
import Upload from "components/shared/Upload";

export default function EditProductsCategory() {
  const { products_category_id } = useParams();

  const navigate = useNavigate();

  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categorySchema),
  });

  // category image state
  const [productCategoryImage, setProductCategoryImage] = useState({
    url: null,
    isChanged: false,
  });

  // image handler
  const imageHandler = (val) => {
    validateImg(val);
    setProductCategoryImage({ url: val, isChanged: true });
  };

  // show modal state
  const [showModal, setShowModal] = useState(false);

  // show modal handler
  const handleShowModal = () => setShowModal((state) => !state);

  // delete item handler
  const handleDeleteItem = async (id) => {
    const res = await request({
      url: () => deleteProductsCategoryRequest(id),
    });
    if (res) {
      reset({
        title: "",
      });
      Toast({ type: "success", title: "حذف سردسته محصولات" });
      navigate("/products/category");
    }
    handleShowModal();
  };

  // get category data
  const getCategoriesData = useCallback(async () => {
    setLoading(true);

    const res = await request({
      url: getProductCategoryByIdRequest,
      values: {
        id: products_category_id,
      },
    });

    if (res) {
      reset({
        title: res.data.title,
      });

      if (res.data.image) {
        setProductCategoryImage({ url: res.data.image, isWeb: true });
      }
    }

    setLoading(false);
  }, [request]);

  // edit category
  const onSubmit = async (data) => {
    const res = await request({
      url: () =>
        editProductsCategoryRequest(
          { ...data, id: products_category_id, image: productCategoryImage },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });

    if (res) {
      reset({
        title: "",
      });

      Toast({ type: "success", title: "ویرایش سردسته " });
      navigate("/products/category");
    }
    setLoading(false);
  };

  useEffect(() => {
    getCategoriesData();
  }, [getCategoriesData]);

  return (
    <DrawerCM title="ویرایش سردسته محصولات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route="/products/category" />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>
                  ویرایش سردسته محصولات
                  <StyledLink
                    to={`/products/category/${products_category_id}/subcategory`}
                  >
                    <Button variant="contained" mt={1}>
                      زیردسته ها
                    </Button>
                  </StyledLink>
                </StyledTitle>
                <Grid container>
                  <Grid item xs={12} p={1.5}>
                    <TextField
                      label="عنوان سردسته"
                      variant="outlined"
                      fullWidth
                      id="title"
                      {...register("title")}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  </Grid>
                  <Grid item md={6} xs={12} p={1.2}>
                    {productCategoryImage.url ? (
                      <Upload
                        isWeb={productCategoryImage.isWeb}
                        isLocal={!productCategoryImage.isWeb}
                        file={productCategoryImage.url}
                        refillable
                        type="add-image"
                        onChange={imageHandler}
                        width={"100%"}
                        height={"100%"}
                        objectFit="contain"
                      />
                    ) : (
                      <Upload
                        isLocal={productCategoryImage.isChanged}
                        file={productCategoryImage.url}
                        refillable
                        type="add-image"
                        onChange={imageHandler}
                        width={"100%"}
                        height={"100%"}
                        objectFit="contain"
                      />
                    )}
                  </Grid>
                  <ButtonContainer item xs={12}>
                    <Button
                      color="success"
                      startIcon={<CheckIcon />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      ثبت
                    </Button>
                    <Button
                      color="error"
                      endIcon={<MdDelete />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowModal();
                      }}
                    >
                      حذف
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>
            </StyledStack>
          </Grid>
          <DeleteModal
            show={showModal}
            onClose={handleShowModal}
            onDelete={handleDeleteItem.bind(null, products_category_id)}
          />
        </Fragment>
      )}
    </DrawerCM>
  );
}
